import { getReservations } from "../api/reservationsService";
import {
   getMyPracticalExamHistory,
   getMyPracticalExamReservation,
} from "../api/practicalExamSlotsService";
import {
   buildLessonCardItem,
   buildPracticalCardItem,
   readPracticalStartValue,
} from "./studentReservationCardUtils";
import { mapReservationsToEvents, toFloatingDate } from "./studentCalendarUtils";

function normalizeItems(raw) {
   if (Array.isArray(raw)) return raw;
   if (!raw || typeof raw !== "object") return [];

   const candidates = [
      raw.items,
      raw.data,
      raw.results,
      raw.rows,
      raw.reservations,
      raw.list,
   ];

   for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
      if (candidate && typeof candidate === "object") {
         if (Array.isArray(candidate.items)) return candidate.items;
         if (Array.isArray(candidate.data)) return candidate.data;
         if (Array.isArray(candidate.results)) return candidate.results;
         if (Array.isArray(candidate.rows)) return candidate.rows;
         if (Array.isArray(candidate.reservations)) return candidate.reservations;
         if (Array.isArray(candidate.list)) return candidate.list;
      }
   }

   return [];
}

function unwrapOne(raw) {
   if (!raw || typeof raw !== "object") return null;
   if (raw.data === null || raw.reservation === null || raw.item === null) {
      return null;
   }
   if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
      return raw.data;
   }
   if (
      raw.reservation &&
      typeof raw.reservation === "object" &&
      !Array.isArray(raw.reservation)
   ) {
      return raw.reservation;
   }
   if (raw.item && typeof raw.item === "object" && !Array.isArray(raw.item)) {
      return raw.item;
   }
   if (raw.id != null || raw._id != null || raw.slotId != null) return raw;
   return null;
}

function isSameCalendarExam(left, right) {
   if (!left || !right) return false;
   if (left.id != null && right.id != null) return String(left.id) === String(right.id);
   return left.start?.getTime?.() === right.start?.getTime?.();
}

function mapPracticalExamToEvent(reservation, index, options = {}) {
   const cardItem = buildPracticalCardItem(reservation, index, options);
   if (!cardItem) return null;

   const start = toFloatingDate(readPracticalStartValue(reservation));
   if (!start) return null;

   const end = new Date(start.getTime() + 60 * 60 * 1000);

   return {
      id: cardItem.id,
      kind: "practicalExam",
      typeLabel: "Examen",
      title: "Examen",
      start,
      end,
      sector: cardItem.sector,
      gearbox: cardItem.gearbox,
      raw: reservation,
      cardItem,
   };
}

function attachLessonCardItems(events) {
   return events.map((event, index) => ({
      ...event,
      kind: "lesson",
      typeLabel: "Lecție",
      title: "Lecție",
      cardItem: buildLessonCardItem(event.raw || event, index),
   }));
}

export async function loadStudentCalendarEvents() {
   const [regularResult, activeExamResult, examHistoryResult] =
      await Promise.allSettled([
         getReservations(),
         getMyPracticalExamReservation(),
         getMyPracticalExamHistory(),
      ]);

   const events = [];

   if (regularResult.status === "fulfilled") {
      events.push(...attachLessonCardItems(mapReservationsToEvents(regularResult.value)));
   }

   const practicalEvents = [];

   if (activeExamResult.status === "fulfilled") {
      const activeExam = unwrapOne(activeExamResult.value);
      const built = mapPracticalExamToEvent(activeExam, 0, { current: true });
      if (built) practicalEvents.push(built);
   }

   if (examHistoryResult.status === "fulfilled") {
      normalizeItems(examHistoryResult.value).forEach((item, index) => {
         const built = mapPracticalExamToEvent(item, index + 1);
         if (!built) return;
         if (practicalEvents.some((existing) => isSameCalendarExam(existing, built))) {
            return;
         }
         practicalEvents.push(built);
      });
   }

   return [...events, ...practicalEvents];
}

export function getLessonEvents(events = []) {
   return events.filter((event) => event.kind === "lesson");
}
